import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { salaryAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, DollarSign, RefreshCw, TrendingUp, 
  Target, Lightbulb, CheckCircle, MessageSquare, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SalaryCoachPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [jobTitle, setJobTitle] = useState('');
  const [location, setLocation] = useState('Gauteng');
  const [experience, setExperience] = useState(2);
  const [currentOffer, setCurrentOffer] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const provinces = [
    'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
    'Free State', 'Mpumalanga', 'Limpopo', 'North West', 'Northern Cape'
  ];

  const calculateSalary = async () => {
    if (!jobTitle.trim()) {
      toast.error('Please enter a job title');
      return;
    }

    setLoading(true);
    try {
      const res = await salaryAPI.calculate({
        job_title: jobTitle,
        location: location,
        experience_years: experience,
        current_offer: currentOffer ? parseInt(currentOffer.replace(/[^\d]/g, '')) : null
      });
      setResult(res.data);
    } catch (error) {
      toast.error('Failed to calculate salary');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Salary Negotiation Coach</h2>
          <p className="text-slate-600 mb-6">Know your worth and negotiate better. Subscribe to access this feature.</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Salary Negotiation Coach</h1>
            <p className="text-sm text-slate-500">Know your worth in the SA job market</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-8">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Calculate Your Worth</h2>
                <p className="text-sm text-slate-500">Get SA market salary data</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Job Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Job Title
                </label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g., Administrative Assistant"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="salary-job-title"
                />
              </div>

              {/* Province */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Province
                </label>
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="salary-location"
                >
                  {provinces.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              {/* Experience */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Years of Experience: {experience}
                </label>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={experience}
                  onChange={(e) => setExperience(parseInt(e.target.value))}
                  className="w-full accent-emerald-600"
                  data-testid="salary-experience"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>Entry Level</span>
                  <span>10 years</span>
                  <span>20+ years</span>
                </div>
              </div>

              {/* Current Offer (optional) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Current Offer (Optional)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">R</span>
                  <input
                    type="text"
                    value={currentOffer}
                    onChange={(e) => setCurrentOffer(e.target.value)}
                    placeholder="e.g., 15000"
                    className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="salary-current-offer"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Enter if you've received a job offer</p>
              </div>

              <Button
                onClick={calculateSalary}
                disabled={loading || !jobTitle.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-700 py-6"
                data-testid="calculate-salary-btn"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <TrendingUp className="w-5 h-5 mr-2" />
                )}
                Calculate Salary Range
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {result ? (
              <>
                {/* Salary Range Card */}
                <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white">
                  <p className="text-emerald-100 text-sm mb-1">Market Salary Range for</p>
                  <h3 className="text-xl font-bold mb-4">{result.job_title} in {result.location}</h3>
                  
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-white/20 rounded-xl p-3 text-center">
                      <p className="text-xs text-emerald-100">Entry</p>
                      <p className="text-lg font-bold">{formatCurrency(result.market_rate?.minimum || result.salary_range?.min || 0)}</p>
                    </div>
                    <div className="bg-white/30 rounded-xl p-3 text-center">
                      <p className="text-xs text-emerald-100">Median</p>
                      <p className="text-xl font-bold">{formatCurrency(result.market_rate?.typical || result.salary_range?.median || 0)}</p>
                    </div>
                    <div className="bg-white/20 rounded-xl p-3 text-center">
                      <p className="text-xs text-emerald-100">Senior</p>
                      <p className="text-lg font-bold">{formatCurrency(result.market_rate?.maximum || result.salary_range?.max || 0)}</p>
                    </div>
                  </div>

                  {result.your_estimated_salary && (
                    <div className="bg-white/20 rounded-xl p-4">
                      <p className="text-sm text-emerald-100">Based on {experience} years experience:</p>
                      <p className="text-2xl font-bold">{formatCurrency(result.your_estimated_salary)}/month</p>
                    </div>
                  )}

                  {/* Important Notice - Inside salary card */}
                  {result.salary_notes && (
                    <div className="mt-4 bg-white/10 rounded-xl p-3 border border-white/20">
                      <p className="text-emerald-100 text-xs flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        <span>{result.salary_notes.disclaimer}</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Salary Factors - Compact version right after salary */}
                {result.salary_notes && (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      Actual salary depends on:
                    </p>
                    <div className="space-y-2">
                      {result.salary_notes.factors?.map((item, i) => (
                        <div key={i} className="bg-white rounded-lg p-3 border border-slate-100">
                          <p className="font-semibold text-slate-800 text-sm">{item.factor}</p>
                          <p className="text-slate-600 text-sm">{item.impact}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-3 italic bg-amber-50 rounded-lg p-2 border border-amber-100">
                      💡 {result.salary_notes.tip}
                    </p>
                  </div>
                )}

                {/* Offer Assessment */}
                {result.offer_assessment && (
                  <div className={`rounded-2xl p-6 border-2 ${
                    result.offer_assessment.status === 'above_market' 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : result.offer_assessment.status === 'below_market'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        result.offer_assessment.status === 'above_market' 
                          ? 'bg-emerald-100' 
                          : result.offer_assessment.status === 'below_market'
                            ? 'bg-red-100'
                            : 'bg-amber-100'
                      }`}>
                        <Target className={`w-5 h-5 ${
                          result.offer_assessment.status === 'above_market' 
                            ? 'text-emerald-600' 
                            : result.offer_assessment.status === 'below_market'
                              ? 'text-red-600'
                              : 'text-amber-600'
                        }`} />
                      </div>
                      <div>
                        <h4 className={`font-semibold ${
                          result.offer_assessment.status === 'above_market' 
                            ? 'text-emerald-800' 
                            : result.offer_assessment.status === 'below_market'
                              ? 'text-red-800'
                              : 'text-amber-800'
                        }`}>
                          {result.offer_assessment.title}
                        </h4>
                        <p className={`text-sm ${
                          result.offer_assessment.status === 'above_market' 
                            ? 'text-emerald-700' 
                            : result.offer_assessment.status === 'below_market'
                              ? 'text-red-700'
                              : 'text-amber-700'
                        }`}>
                          {result.offer_assessment.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Negotiation Tips */}
                {result.negotiation_tips && result.negotiation_tips.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Lightbulb className="w-6 h-6 text-amber-500" />
                      <h3 className="font-semibold text-slate-900">Negotiation Tips</h3>
                    </div>
                    <ul className="space-y-3">
                      {result.negotiation_tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Scripts from backend */}
                {result.scripts && (result.scripts.benefits || result.scripts.delay) && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <MessageSquare className="w-6 h-6 text-blue-500" />
                      <h3 className="font-semibold text-slate-900">What to Say</h3>
                    </div>
                    <div className="space-y-3">
                      {result.scripts.benefits && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <p className="text-sm font-medium text-blue-800 mb-1">Negotiating Benefits</p>
                          <p className="text-blue-700 italic">"{result.scripts.benefits}"</p>
                        </div>
                      )}
                      {result.scripts.delay && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <p className="text-sm font-medium text-blue-800 mb-1">Asking for Time</p>
                          <p className="text-blue-700 italic">"{result.scripts.delay}"</p>
                        </div>
                      )}
                      {result.scripts.counter_offer && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <p className="text-sm font-medium text-blue-800 mb-1">Counter Offer</p>
                          <p className="text-blue-700 italic">"{result.scripts.counter_offer}"</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Scripts (legacy format) */}
                {result.negotiation_scripts && result.negotiation_scripts.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <MessageSquare className="w-6 h-6 text-blue-500" />
                      <h3 className="font-semibold text-slate-900">What to Say</h3>
                    </div>
                    <div className="space-y-3">
                      {result.negotiation_scripts.map((script, i) => (
                        <div key={i} className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <p className="text-sm font-medium text-blue-800 mb-1">{script.scenario}</p>
                          <p className="text-blue-700 italic">"{script.script}"</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Placeholder */
              <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Get Your Salary Insights</h3>
                <p className="text-slate-500">
                  Enter your job details to see market salary ranges and negotiation tips for South Africa.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SalaryCoachPage;
